import React, { useState } from "react";
import { Mail, ExternalLink, FileText, BookOpen, Send, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitForm } from "../lib/submit";

// Real, publicly available articles & references on high-polyphenol olive oil.
// These are NOT press coverage of FOYEVOO — they are general industry reading.
const READING_LIST = [
  {
    title: "Why every pro athlete takes olive oil",
    publication: "The Feed",
    url: "https://thefeed.com/insider/why-every-pro-athlete-takes-olive-oil",
    type: "Article",
  },
  {
    title: "High-phenolic olive oils: a powerful superfood",
    publication: "Olive Oil Professor",
    url: "https://oliveoilprofessor.com/blog/high-phenolic-olive-oils-a-powerful-superfood",
    type: "Article",
  },
  {
    title: "Olive oil: tradition and triumph in sports",
    publication: "About Olive Oil",
    url: "https://www.aboutoliveoil.org/olive-oil-tradition-and-triumph-in-sports",
    type: "Article",
  },
  {
    title: "Primary effects of extra virgin olive oil consumption (review)",
    publication: "PubMed Central / NIH",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4139846/",
    type: "Peer-reviewed",
  },
  {
    title: "PREDIMED: Mediterranean diet & cardiovascular events",
    publication: "New England Journal of Medicine",
    url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1800389",
    type: "Peer-reviewed",
  },
  {
    title: "EU Regulation 432/2012 \u2014 olive oil polyphenol health claim",
    publication: "EUR-Lex (European Union)",
    url: "https://eur-lex.europa.eu/legal-content/EN/ALL/?uri=CELEX:32012R0432",
    type: "Regulation",
  },
  {
    title: "Blueprint olive oil (Bryan Johnson)",
    publication: "blueprint.bryanjohnson.com",
    url: "https://blueprint.bryanjohnson.com/products/extra-virgin-olive-oil",
    type: "Public protocol",
  },
];

export default function PressPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", outlet: "", email: "", message: "", honey: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message || busy) return;
    setBusy(true);

    await submitForm(
      {
        name: form.name,
        outlet_or_organisation: form.outlet,
        email: form.email,
        message: form.message,
      },
      {
        subject: `FOYEVOO Press / Media enquiry \u2014 ${form.name || form.email}`,
        source: "Press Page",
        honeypot: form.honey,
      }
    );

    setSubmitted(true);
    setBusy(false);
    toast({ title: "Message sent", description: "We'll be in touch shortly." });
  };

  return (
    <div className="bg-[#f4ede0] text-[#1a2a38] min-h-screen">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-12 text-center">
        <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">Press &amp; Media</p>
        <h1 className="text-4xl md:text-6xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          For journalists, researchers, and partners.
        </h1>
        <p className="mt-6 text-[#3a4a58] leading-relaxed max-w-2xl mx-auto">
          Fountain of Youth is a newly-launched producer of ultra-high phenolic extra virgin olive oil. We're happy to share lab certificates, founder interviews, and product samples with credentialed media.
        </p>
      </section>

      {/* Contact form + Press kit */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16 grid md:grid-cols-2 gap-6">
        {/* Contact Form */}
        <div className="bg-[#1e3a52] text-[#f4ede0] rounded-sm p-8">
          <Send size={22} className="text-[#d4a857]" strokeWidth={1.5} />
          <h3 className="mt-4 text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Media enquiries</h3>
          <p className="mt-2 text-sm text-[#f4ede0]/80 leading-relaxed">
            Drop us a note for interview requests, product samples, partnership opportunities, or fact-checking.
          </p>

          {submitted ? (
            <div className="mt-6 p-5 bg-[#0f2c44] border border-[#d4a857]/30 rounded-sm text-center">
              <Check size={24} className="mx-auto text-[#d4a857]" />
              <h4 className="mt-2 text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Message sent.</h4>
              <p className="mt-1 text-sm text-[#f4ede0]/75">We'll respond to {form.email} within 48 hours.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-5 grid gap-3">
              {/* Honeypot */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.honey}
                onChange={(e) => setForm({ ...form, honey: e.target.value })}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="px-4 py-2.5 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] placeholder-[#f4ede0]/40 focus:outline-none focus:border-[#d4a857]"
              />
              <input
                value={form.outlet}
                onChange={(e) => setForm({ ...form, outlet: e.target.value })}
                placeholder="Publication / organisation"
                className="px-4 py-2.5 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] placeholder-[#f4ede0]/40 focus:outline-none focus:border-[#d4a857]"
              />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Your email *"
                className="px-4 py-2.5 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] placeholder-[#f4ede0]/40 focus:outline-none focus:border-[#d4a857]"
              />
              <textarea
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Your message *"
                rows={4}
                className="px-4 py-2.5 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] placeholder-[#f4ede0]/40 focus:outline-none focus:border-[#d4a857] resize-none"
              />
              <button
                type="submit"
                disabled={busy}
                className="bg-[#d4a857] hover:bg-[#b8893d] disabled:opacity-60 text-[#0f2c44] py-2.5 rounded-sm font-medium transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
              >
                {busy ? "Sending\u2026" : "Send message"}
              </button>
            </form>
          )}
        </div>

        {/* Press Kit card */}
        <div className="bg-[#ebe2cd] border border-[#4a7ba1]/20 rounded-sm p-8 flex flex-col">
          <FileText size={22} className="text-[#1e3a52]" strokeWidth={1.5} />
          <h3 className="mt-4 text-2xl text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Press kit</h3>
          <p className="mt-2 text-[#3a4a58] leading-relaxed">
            Logos, product photography, founder bios, and the latest harvest certificate of analysis &mdash; bundled in a single download.
          </p>
          <p className="mt-auto pt-5 text-sm text-[#3a4a58]/80 italic leading-relaxed">
            Press kit coming soon. In the meantime, please use the contact form for any specific assets you need.
          </p>
        </div>
      </section>

      {/* Recommended reading */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">Recommended reading</p>
          <h2 className="text-3xl md:text-4xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Real, public sources on high-polyphenol olive oil.
          </h2>
          <p className="mt-4 text-sm text-[#3a4a58]">
            A curated list of independent articles, studies and regulations. These are background reading on the category &mdash; they are <strong>not</strong> press coverage of Fountain of Youth.
          </p>
        </div>

        <div className="space-y-3">
          {READING_LIST.map((r, i) => (
            <a
              key={i}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-5 p-5 bg-[#f4ede0] border border-[#4a7ba1]/15 hover:border-[#d4a857]/50 hover:bg-[#ebe2cd]/50 rounded-sm transition-all"
            >
              <div className="w-11 h-11 rounded-sm bg-[#1e3a52] grid place-items-center flex-shrink-0">
                <BookOpen size={18} className="text-[#d4a857]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-[#4a7ba1] bg-[#4a7ba1]/10 px-2 py-0.5 rounded-sm">{r.type}</span>
                  <span className="text-xs text-[#3a4a58]/70">{r.publication}</span>
                </div>
                <div className="text-lg text-[#1e3a52] group-hover:text-[#d4a857] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {r.title}
                </div>
              </div>
              <ExternalLink size={16} className="text-[#4a7ba1] mt-3 flex-shrink-0 group-hover:text-[#d4a857]" />
            </a>
          ))}
        </div>

        <div className="mt-12 p-6 bg-[#ebe2cd] border-l-4 border-[#d4a857] rounded-sm">
          <p className="text-sm text-[#3a4a58] leading-relaxed">
            <strong className="text-[#1e3a52]">Note for journalists:</strong> any quotes attributed to Fountain of Youth, our founders, or our customers will be added to this page only once they are on the public record. Until then, the only on-record references on this site are the four cited on{" "}
            <a href="/the-edge#testimonials" className="underline text-[#1e3a52] hover:text-[#d4a857]">The Edge</a>{" "}
            (Bryan Johnson, Kilian Jornet, the PREDIMED trial, and the EFSA health claim) &mdash; each with its source link.
          </p>
        </div>
      </section>
    </div>
  );
}
